import React from 'react';
import { Link } from 'react-router-dom';
import { makeStyles } from '@mui/styles';
import clsx from 'clsx';
import maybe from '../../../components/@common/enhancers/maybe';
import FavoritesBadge from '../../../components/Badges/FavoritesBadge';
import { black, lwtBlue, darkGray, lwtDarkBlue } from '../../../styles/style-constants';
import { remCalc } from '../../../util/style-util';

type Props = {
  label: string;
  active: boolean;
  top: number;
  icon?: any;
  link: string;
  sideMargin?: number;
  favoritesBadge: boolean | null;
  pinnedItems: number;
};

const getMargin = (props): string => {
  return `
    0
    ${props.sideMargin ? `${props.sideMargin}px` : '0'}
    ${props.active ? '0' : '3px'}
    ${props.sideMargin ? `${props.sideMargin}px` : '0'}
  `;
};

function TabLinkButton(props: Props | any): React.ReactElement {
  const { icon, active, link, label, sideMargin, pinnedItems, favoritesBadge } = props;
  const { tabLinkButton, activeTabLinkButton, sideMarginValues, iconContainer } = useStyles({
    margin: getMargin(props),
    top: props.top
  });

  const getFillIconColor = (): string => {
    return !active ? darkGray : active ? lwtDarkBlue : black;
  };

  const renderIconComponent = (IconComponent: any, fill: string): React.ReactElement | null => {
    return IconComponent ? <IconComponent fill={fill} /> : null;
  };
  const MaybeFavoritesBadge: any = maybe(FavoritesBadge);

  return (
    <Link
      className={clsx(tabLinkButton, active && activeTabLinkButton, sideMargin && sideMarginValues)}
      to={link}
    >
      <div className={iconContainer}>{renderIconComponent(icon, getFillIconColor())}</div>
      <span>{label}</span>
      <MaybeFavoritesBadge
        shouldRender={!!favoritesBadge && pinnedItems !== 0}
        pinnedItems={pinnedItems}
      />
    </Link>
  );
}

TabLinkButton.defaultProps = {
  top: 0
};

const useStyles = makeStyles(() => ({
  tabLinkButton: ({ top }: any) => ({
    position: 'relative',
    top: top,
    fontSize: remCalc(20),
    padding: remCalc(6),
    paddingTop: remCalc(6),
    paddingBottom: remCalc(6),
    paddingRight: remCalc(50),
    paddingLeft: remCalc(20),
    color: black,
    userSelect: 'none',
    marginBottom: '3px',
    cursor: 'pointer',
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    fontWeight: 400,
    minWidth: '150px',
    zIndex: 1,
    textDecoration: 'none !important',
    ':focus': {
      outline: 'none',
      textDecoration: 'none !important',
      color: `${lwtBlue} !important`
    },
    ':visited': {
      textDecoration: 'none !important'
    },
    ':active': {
      textDecoration: 'none !important',
      color: `${lwtBlue} !important`
    },
    ':hover': {
      textDecoration: 'none !important',
      color: `${black} !important`
    },
    ':link': {
      textDecoration: 'none !important'
    }
  }),
  activeTabLinkButton: {
    color: `${lwtBlue} !important`,
    borderBottom: `3px solid ${lwtBlue}`,
    fontWeight: 500,
    textDecoration: 'none !important'
  },
  iconContainer: {
    position: 'relative',
    width: remCalc(24),
    height: remCalc(24),
    marginRight: '10px'
  },
  sideMarginValues: ({ margin }) => ({
    margin
  }),
  disabledLink: {
    pointerEvents: 'none',
    display: 'none'
  }
}));

export default TabLinkButton;
